// Ad-hoc aggregate leaderboard PDF (A4 portrait, 1 page).
// Kept out of `/app/frontend/src/lib/pdf.js` so its column-width logic
// doesn't have to co-exist with the scoresheet + results renderers.
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDMY } from "./date";
import { aggregateFilenameStem } from "./aggregateFilename";

const A4_PORTRAIT_WIDTH_MM = 210;
const SIDE_MARGIN_MM = 10;

// Function name is kept for backwards compatibility with the modal;
// orientation is A4 portrait even though the identifier still says
// "Landscape" — renaming would require touching every import site.
export function savePdfA4Landscape_Aggregate({ clubName, competitions, leaderboard }) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const centerX = A4_PORTRAIT_WIDTH_MM / 2;
  let y = 12;

  // Human-readable label used both as the on-page subtitle AND the PDF's
  // internal document title metadata — Safari, Preview.app and Adobe
  // Reader all show `doc.setProperties({title})` as the viewer tab
  // title, so this must reflect the competitions the user picked.
  const compsSummary = competitions
    .map((c) => c.competition_name)
    .join(" · ");
  doc.setProperties({
    title: `Aggregate — ${compsSummary}`.slice(0, 200),
    subject: "Camera Club Score — aggregate leaderboard",
    author: clubName || "Camera Club Score",
    creator: "Camera Club Score",
  });

  if (clubName) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(17);
    doc.text(clubName, centerX, y, { align: "center" });
    y += 6;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(17);
  doc.text(
    `Aggregate leaderboard — top ${leaderboard.length} across ${competitions.length} competitions`,
    centerX, y, { align: "center" }
  );
  y += 5;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(80);
  const compsLine = competitions
    .map((c) => `${c.competition_name} (${formatDMY(c.competition_date)})`)
    .join("   ·   ");
  const wrapped = doc.splitTextToSize(compsLine, A4_PORTRAIT_WIDTH_MM - 2 * SIDE_MARGIN_MM);
  doc.text(wrapped, centerX, y, { align: "center" });
  y += wrapped.length * 4 + 1;

  doc.setDrawColor(17);
  doc.setLineWidth(0.4);
  doc.line(SIDE_MARGIN_MM, y, A4_PORTRAIT_WIDTH_MM - SIDE_MARGIN_MM, y);
  y += 3;

  const head = [[
    "#",
    "I.D.",
    "Photographer",
    ...competitions.map((c) => c.competition_name),
    "Total",
  ]];

  // Body cells carry ONLY the score as a string — the image title is
  // stashed on `_titles` so `didDrawCell` can render it in a smaller,
  // muted italic below the score without fighting autoTable's own
  // line-wrapping. Keeping the title out of `raw` avoids autoTable
  // computing the row height as if it were regular body text.
  const body = leaderboard.map((r) => [
    String(r.rank),
    r.photographer_code || "",
    r.photographer_name || "",
    ...competitions.map((c) => {
      const v = r.per_competition?.[c.id];
      return v == null ? "" : String(v);
    }),
    String(r.total),
  ]);

  // Parallel array indexed by [row][compIdx] used inside didDrawCell.
  const rowTitles = leaderboard.map((r) =>
    competitions.map((c) => r.per_competition_title?.[c.id] || ""),
  );

  autoTable(doc, {
    startY: y,
    head,
    body,
    margin: { left: SIDE_MARGIN_MM, right: SIDE_MARGIN_MM, bottom: 10 },
    theme: "grid",
    headStyles: {
      fillColor: [243, 243, 243],
      textColor: [34, 34, 34],
      fontStyle: "bold",
      fontSize: 8,
      lineColor: [153, 153, 153],
      lineWidth: 0.2,
      halign: "left",
      cellPadding: 1.5,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 1.6,
      textColor: [17, 17, 17],
      lineColor: [153, 153, 153],
      lineWidth: 0.2,
      // Room for the score plus one to two lines of image title beneath it.
      minCellHeight: 12,
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 9, textColor: [102, 102, 102] },
      1: { fontStyle: "bold", cellWidth: 14 },
      2: { cellWidth: 42 },
      // Per-competition columns are left to auto-fit the remaining
      // width; the Total column at the right is a fixed 18mm.
      [competitions.length + 3]: { halign: "right", fontStyle: "bold", cellWidth: 18 },
    },
    didParseCell: (data) => {
      if (data.section !== "body") return;
      // Right-align every per-competition score cell + Total.
      if (data.column.index >= 3) data.cell.styles.halign = "right";
      // Score cells anchor the score to the top so we can render the
      // image title beneath it in didDrawCell without collisions.
      const compCount = competitions.length;
      if (data.column.index >= 3 && data.column.index < 3 + compCount) {
        data.cell.styles.valign = "top";
      }
      // Winner row: highlight in amber tint.
      if (data.row.index === 0) data.cell.styles.fillColor = [254, 243, 199];
    },
    didDrawCell: (data) => {
      if (data.section !== "body") return;
      const compCount = competitions.length;
      const compIdx = data.column.index - 3;
      if (compIdx < 0 || compIdx >= compCount) return;
      const title = rowTitles[data.row.index]?.[compIdx];
      if (!title) return;
      const padX = data.cell.padding("horizontal");
      const padY = data.cell.padding("vertical");
      const innerW = data.cell.width - padX * 2;
      doc.setFont("helvetica", "italic");
      doc.setFontSize(6.5);
      doc.setTextColor(110);
      const wrapped = doc.splitTextToSize(title, innerW);
      // Draw up to two lines of title beneath the score. The score line
      // (font-size 9) is about 3.2mm high — we leave ~3.8mm before the
      // title starts so the two never overlap.
      doc.text(
        wrapped.slice(0, 2),
        data.cell.x + data.cell.width - padX,
        data.cell.y + padY + 4.2,
        { align: "right" },
      );
      // Reset defaults for the next cell autoTable draws.
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(17, 17, 17);
    },
  });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(140);
  const footY = doc.internal.pageSize.getHeight() - 6;
  doc.text(
    `Camera Club Score · aggregate leaderboard · ${new Date().toLocaleString()}`,
    centerX, footY, { align: "center" }
  );

  const safe = `${aggregateFilenameStem(competitions)}.pdf`;
  doc.save(safe);
}
